import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  Avatar,
  Box,
  Checkbox,
  MenuItem,
  Paper,
  Popper,
} from '@mui/material'
import InputBase from '@mui/material/InputBase'
import { ICellEditorParams } from 'ag-grid-community'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import _ from 'lodash'
import { gridRootStyle } from '../../../commons/AgGrid'
import SaveButton from '../../../../components/buttons/SaveButton'
import CancelButton from '../../../../components/buttons/CancelButton'
import Tooltip from '../../../../components/tooltips/Tooltip'

interface AutocompleteOption {
  uuid: string
  name: string
  iconUrl: string
}

interface Props extends ICellEditorParams {
  search: (text: string, projectUuid: string, data: any) => Promise<any>
  label: (option: AutocompleteOption) => string
  sortOptions?: (options: AutocompleteOption[]) => AutocompleteOption[]
  showIcon?: boolean
  disableOption?: (
    params: ICellEditorParams,
    option: AutocompleteOption
  ) => boolean
  optionTooltip?: (
    params: ICellEditorParams,
    option: AutocompleteOption
  ) => string | undefined
  noOptionsText?: string
}

export const MultiAutocompleteCellEditor = forwardRef((props: Props, ref) => {
  const inputRef = React.createRef<HTMLInputElement>()
  const listboxRef = useRef<HTMLUListElement | null>(null)
  const scrollPositionRef = useRef<number>(0)

  const [value, setValue] = useState<AutocompleteOption[]>(props.value)
  const [text, setText] = useState<string>(
    (props.eventKey?.length === 1 ? props.eventKey : null) ?? ''
  )
  const [suggestions, setSuggestions] = useState<AutocompleteOption[]>([])
  const [open, setOpen] = useState<boolean>(false)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const search = useMemo(
    () =>
      _.debounce(async (value: string) => {
        const options = await props.search(
          value,
          props.data?.body?.wbsItem?.projectUuid,
          props.data
        )
        // 候補0件でもポップアップを開く（No optionsを表示するため）
        setOpen(true)
        setSuggestions(props.sortOptions ? props.sortOptions(options) : options)
      }, 300),
    [props.noOptionsText]
  )

  const focused = () => {
    search(text ?? '')
  }

  const inputChanged = useCallback(
    (
      event: React.ChangeEvent<{}>,
      value: string,
      reason: AutocompleteInputChangeReason
    ) => {
      if (reason === 'input') {
        setText(value)
        search(value)
      }
    },
    []
  )

  const valueChanged = useCallback(
    (
      event: React.ChangeEvent<{}>,
      option: AutocompleteOption[] | null,
      reason: AutocompleteChangeReason
    ) => {
      // スクロール位置を保存
      if (listboxRef.current) {
        scrollPositionRef.current = listboxRef.current.scrollTop
      }
      setValue(option || [])
      setText('')
      // 次のレンダリング後にスクロール位置を復元
      requestAnimationFrame(() => {
        if (listboxRef.current) {
          listboxRef.current.scrollTop = scrollPositionRef.current
        }
      })
    },
    []
  )

  const onSubmit = useCallback(() => {
    setOpen(false)
    props.api.stopEditing()
  }, [])

  const onCancel = useCallback(() => {
    setOpen(false)
    props.api.stopEditing(true)
  }, [])

  const onClose = useCallback((e, reason) => {
    if (['blur', 'toggleInput'].includes(reason)) {
      return
    }
    let cancel = false
    if (reason === 'escape') {
      cancel = true
    }
    setOpen(false)
    props.api.stopEditing(cancel)
  }, [])

  useImperativeHandle(ref, () => {
    return {
      getValue() {
        return value
      },
    }
  })

  const getLabel = props.label
  const showIcon = props.showIcon ?? true
  const disableOption = props.disableOption
  const optionTooltip = props.optionTooltip
  const cellEditorParams = props

  return (
    <Box sx={gridRootStyle}>
      <Autocomplete
        multiple={true}
        options={suggestions}
        getOptionLabel={option => (option.name ? option.name : '')}
        renderOption={(props, option: AutocompleteOption, { selected }) => {
          const optionComponent = (
            <MenuItem
              {...props}
              sx={{
                display: 'flex',
                justifyContent: 'start',
                alignItems: 'center',
                '& .MuiCheckbox-root': { marginRight: '0px !important' },
                '& .MuiAvatar-root': {
                  margin: '0px 6px 0px 0px !important',
                },
              }}
              disabled={
                disableOption ? disableOption(cellEditorParams, option) : false
              }
            >
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
                color={'primary'}
              />
              {showIcon && (
                <Avatar
                  variant="circular"
                  sx={{
                    marginRight: '5px',
                    height: '23px',
                    width: '23px',
                    borderRadius: '50%',
                  }}
                  src={option.iconUrl}
                />
              )}
              <Box sx={{ whiteSpace: 'nowrap' }}>{getLabel(option)}</Box>
            </MenuItem>
          )
          const tooltip = optionTooltip
            ? optionTooltip(cellEditorParams, option)
            : undefined
          if (tooltip) {
            return (
              <Tooltip message={tooltip}>
                <span>{optionComponent}</span>
              </Tooltip>
            )
          } else {
            return optionComponent
          }
        }}
        isOptionEqualToValue={(value, option) => {
          return value.uuid === option.uuid
        }}
        filterOptions={(options: AutocompleteOption[]) => {
          return options
        }}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <InputBase
            inputRef={inputRef}
            ref={params.InputProps.ref}
            inputProps={params.inputProps}
            autoFocus={true}
          />
        )}
        PopperComponent={props => (
          <Popper {...props} placement="bottom-start" />
        )}
        PaperComponent={paperProps => (
          <Paper
            {...paperProps}
            sx={{
              '& .MuiAutocomplete-option': { paddingLeft: '1px' },
            }}
          >
            {paperProps.children}
            {suggestions.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '36px',
                }}
              >
                <SaveButton onClick={onSubmit} />
                <CancelButton onClick={onCancel} />
              </Box>
            )}
          </Paper>
        )}
        autoSelect={false}
        blurOnSelect={false}
        open={open}
        noOptionsText={props.noOptionsText ?? 'No options'}
        value={value}
        onFocus={focused}
        onInputChange={inputChanged}
        onChange={valueChanged}
        onClose={onClose}
        disableCloseOnSelect={true}
        ListboxProps={{
          ref: listboxRef,
        }}
      />
    </Box>
  )
})
